import { useState } from 'react';
import { Badge, Avatar, Dropdown, Space, Tooltip } from 'antd';
import {
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  LockOutlined,
  SunOutlined,
  MoonOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/auth/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useTheme } from '@/theme/ThemeContext';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import Breadcrumbs from './Breadcrumbs';
import { useNavigate } from 'react-router-dom';
import type { MenuProps } from 'antd';

const TopNav = () => {
  const { user, logout } = useAuth();
  const { unreadCount } = useNotifications();
  const { resolvedTheme, toggle } = useTheme();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const navigate = useNavigate();
  const isDark = resolvedTheme === 'dark';

  const displayName = user?.name || user?.preferred_username || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/profile'),
    },
    {
      key: 'change-password',
      label: 'Change Password',
      icon: <LockOutlined />,
      onClick: () => setChangePasswordOpen(true),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      onClick: logout,
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        height: '100%',
        // Reset the Header's inherited line-height (60px) so the inline icon
        // glyphs, notification badge and avatar align on the flex centre line.
        lineHeight: 1,
      }}
    >
      <Breadcrumbs />
      <Space size="middle">
        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
          <div
            onClick={toggle}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: 'var(--color-bg-hover)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-container)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
          >
            {isDark ? (
              <SunOutlined style={{ fontSize: 16, color: 'var(--color-warning)' }} />
            ) : (
              <MoonOutlined style={{ fontSize: 16, color: 'var(--color-text-secondary)' }} />
            )}
          </div>
        </Tooltip>

        <Badge count={unreadCount} offset={[-5, 5]} size="small">
          <div
            onClick={() => navigate('/notifications')}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: 'var(--color-bg-hover)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-container)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
          >
            <BellOutlined style={{ fontSize: 16, color: 'var(--color-text-secondary)' }} />
          </div>
        </Badge>

        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
          <Space style={{ cursor: 'pointer' }}>
            <Avatar
              style={{
                background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
                fontWeight: 600,
                fontSize: 13,
              }}
              size={34}
            >
              {initials}
            </Avatar>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' }}>
              {displayName}
            </span>
          </Space>
        </Dropdown>
      </Space>
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </div>
  );
};

export default TopNav;
